import { Injectable, inject } from '@angular/core';
import { LOCAL_STORAGE_KEYS } from '../local-storage/local-storage.service';
import { LocalStorageService } from '../local-storage/local-storage.service';

export type AnalyticsConsentStatus = 'pending' | 'allowed' | 'denied';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsConsentService {
  private readonly localStorageService = inject(LocalStorageService);

  /**
   * Gets the current consent status
   * @returns 'pending' if no decision made, 'allowed' if user consented, 'denied' if user declined
   */
  getConsentStatus(): AnalyticsConsentStatus {
    const stored = this.localStorageService.getItem(LOCAL_STORAGE_KEYS.ANALYTICS_CONSENT);
    if (stored === 'allowed') return 'allowed';
    if (stored === 'denied') return 'denied';
    return 'pending';
  }

  /**
   * Checks if user has made a consent decision (either allowed or denied)
   * @returns true if consent decision has been recorded
   */
  hasConsented(): boolean {
    return this.getConsentStatus() !== 'pending';
  }

  /**
   * Checks if analytics are allowed
   * @returns true if user has explicitly allowed analytics
   */
  isAllowed(): boolean {
    return this.getConsentStatus() === 'allowed';
  }

  /**
   * Records the user's consent decision
   * @param allowed - true if user allows analytics, false if denied
   */
  setConsent(allowed: boolean): void {
    const value: AnalyticsConsentStatus = allowed ? 'allowed' : 'denied';
    this.localStorageService.setItem(LOCAL_STORAGE_KEYS.ANALYTICS_CONSENT, value);
  }

  /**
   * Clears the consent decision (for testing or reset purposes)
   */
  clearConsent(): void {
    this.localStorageService.removeItem(LOCAL_STORAGE_KEYS.ANALYTICS_CONSENT);
  }
}
